import { useState, useMemo } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useActivityHeatmap } from '@/hooks/use-progress'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface ActivityDay {
  date: Date
  count: number
  minutesWatched: number
  episodesWatched: number
  episodesCompleted: number
  level: number
}

const getActivityColor = (level: number): string => {
  switch (level) {
    case 0:
      return 'bg-gray-800/30' // Vazio
    case 1:
      return 'bg-orange-900/50' // Muito pouco (1-14 min)
    case 2:
      return 'bg-orange-700/70' // Pouco (15-29 min)
    case 3:
      return 'bg-orange-500' // Moderado (30-59 min)
    case 4:
      return 'bg-orange-400' // Alto (60-89 min)
    case 5:
      return 'bg-orange-300' // Muito alto (90+ min)
    default:
      return 'bg-gray-800/30'
  }
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const getMonthLabels = (data: ActivityDay[]): { label: string; index: number }[] => {
  const months: { label: string; index: number }[] = []
  let currentMonth = -1

  data.forEach((day, index) => {
    const month = day.date.getMonth()
    if (month !== currentMonth) {
      currentMonth = month
      const weekIndex = Math.floor(index / 7)
      months.push({
        label: day.date.toLocaleDateString('pt-BR', { month: 'short' }),
        index: weekIndex,
      })
    }
  })

  return months
}

const ActivityGraph = () => {
  const currentYear = new Date().getFullYear()
  const [selectedYear] = useState(currentYear)

  const { data: heatmapData, isLoading, isError, error } = useActivityHeatmap(selectedYear)

  // Transform API data to component format
  const activityData = useMemo(() => {
    if (!heatmapData) return []

    // Create a map of dates to activity data
    const activityMap = new Map<string, ActivityDay>()

    heatmapData.data.forEach((day) => {
      const date = new Date(day.date)
      activityMap.set(day.date, {
        date,
        count: day.episodesWatched,
        minutesWatched: day.minutesWatched,
        episodesWatched: day.episodesWatched,
        episodesCompleted: day.episodesCompleted,
        level: day.level,
      })
    })

    // Fill in all days of the year
    const startDate = new Date(selectedYear, 0, 1)
    const endDate = new Date(selectedYear, 11, 31)
    const allDays: ActivityDay[] = []

    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0]
      const activity = activityMap.get(dateStr)

      if (activity) {
        allDays.push(activity)
      } else {
        allDays.push({
          date: new Date(date),
          count: 0,
          minutesWatched: 0,
          episodesWatched: 0,
          episodesCompleted: 0,
          level: 0,
        })
      }
    }

    return allDays
  }, [heatmapData, selectedYear])

  const monthLabels = useMemo(() => getMonthLabels(activityData), [activityData])

  // Organize data into weeks (columns) and days (rows)
  const weeks: ActivityDay[][] = useMemo(() => {
    const result: ActivityDay[][] = []
    for (let i = 0; i < activityData.length; i += 7) {
      result.push(activityData.slice(i, i + 7))
    }
    return result
  }, [activityData])

  const dayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-4 w-48" />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Erro ao carregar histórico de atividades: {error?.message || 'Erro desconhecido'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!heatmapData) {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
        <span>
          <strong className="text-foreground">{heatmapData.totalActivities}</strong> atividades no
          último ano
        </span>
        <span>
          <strong className="text-foreground">{heatmapData.activeDays}</strong> dias ativos
        </span>
        {heatmapData.currentStreak > 0 && (
          <span>
            🔥 <strong className="text-foreground">{heatmapData.currentStreak}</strong> dias de
            sequência
          </span>
        )}
      </div>

      {/* Graph Container — ocupa a largura toda do card. Colunas e células
          são responsivas (flex-1 + aspect-square) em vez de tamanho fixo,
          então a grade se estica pra preencher o quadro. */}
      <div className="w-full">
        {/* Month Labels — alinhados às colunas por porcentagem (cada uma das
            N colunas ocupa ~1/N da largura útil). O spacer w-8 casa com a
            coluna de rótulos de dia abaixo. */}
        <div className="mb-1 flex">
          <div className="w-8 shrink-0" />
          <div className="relative h-4 flex-1">
            {monthLabels.map((month, idx) => (
              <div
                key={idx}
                className="absolute top-0 text-xs text-muted-foreground"
                style={{ left: `${(month.index / weeks.length) * 100}%` }}
              >
                {month.label}
              </div>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex gap-1">
          {/* Day Labels — flex-1 por linha pra acompanhar a altura das
              células, que agora é responsiva. */}
          <div className="flex w-8 shrink-0 flex-col gap-[3px]">
            {dayLabels.map((day, idx) => (
              <div
                key={day}
                className="flex flex-1 items-center text-xs text-muted-foreground"
                style={{ visibility: idx % 2 === 1 ? 'visible' : 'hidden' }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Weeks Grid */}
          <div className="flex flex-1 gap-[3px]">
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="flex flex-1 flex-col gap-[3px]">
                {week.map((day, dayIdx) => {
                  return (
                    <Tooltip key={dayIdx}>
                      <TooltipTrigger asChild>
                        <div
                          className={`aspect-square w-full rounded-sm cursor-pointer transition-all hover:ring-1 hover:ring-foreground/50 ${getActivityColor(day.level)}`}
                        />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="text-sm">
                          <p className="font-medium">{formatDate(day.date)}</p>
                          {day.count === 0 ? (
                            <p className="text-muted-foreground">Nenhuma atividade</p>
                          ) : (
                            <div className="mt-1 space-y-0.5">
                              <p>⏱️ {day.minutesWatched} minuto(s) de estudo</p>
                              <p>📺 {day.episodesWatched} episódio(s) assistido(s)</p>
                              {day.episodesCompleted > 0 && (
                                <p>✅ {day.episodesCompleted} episódio(s) completado(s)</p>
                              )}
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Menos</span>
        <div className="flex gap-[3px]">
          {[0, 1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`w-[11px] h-[11px] rounded-sm ${getActivityColor(level)}`}
            />
          ))}
        </div>
        <span>Mais</span>
      </div>

      {/* Activity Stats */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t border-border/30">
        <span>
          📊 Média:{' '}
          <strong className="text-foreground">{heatmapData.averageMinutesPerDay} min/dia</strong>
        </span>
        <span>
          ⏱️ Total:{' '}
          <strong className="text-foreground">{heatmapData.totalMinutesWatched} minutos</strong>
        </span>
        {heatmapData.longestStreak > 0 && (
          <span>
            🏆 Melhor sequência:{' '}
            <strong className="text-foreground">{heatmapData.longestStreak} dias</strong>
          </span>
        )}
      </div>
    </div>
  )
}

export default ActivityGraph
